import { SMLogo } from "@/app/components/sm-logo";
import { requestOpenPreferences } from "@/app/consent/consent-context";
import { APPSTORE_URL, PLAYSTORE_URL } from "@/config/store-urls";
import { Instagram, Mail } from "lucide-react";

export interface PopularCategory {
	categoryId: string;
	name: string;
}

interface FooterProps {
	/** Logo block goes to app home */
	onLogoClick?: () => void;
	onNavigateToAbout?: () => void;
	onNavigateToTerms?: () => void;
	onNavigateToPrivacyPolicy?: () => void;
	/** Public LGPD / Google Play account deletion instructions (same URL as Play Console). */
	onNavigateToAccountDeletion?: () => void;
	onNavigateToHelp?: () => void;
	/** When set, show these categories (top by ad count when API ranks them; else first names A–Z). Click passes categoryId. */
	popularCategories?: PopularCategory[];
	onCategoryClick?: (categoryId: string) => void;
}

export function Footer({
	onLogoClick,
	onNavigateToAbout,
	onNavigateToTerms,
	onNavigateToPrivacyPolicy,
	onNavigateToAccountDeletion,
	onNavigateToHelp,
	popularCategories = [],
	onCategoryClick,
}: FooterProps) {
	const currentYear = new Date().getFullYear();

	const companyLinks = [
		{ label: "Sobre Nós", onClick: onNavigateToAbout },
		{ label: "Central de Ajuda", onClick: onNavigateToHelp },
		{ label: "Termos de Uso", onClick: onNavigateToTerms },
		{ label: "Política de Privacidade", onClick: onNavigateToPrivacyPolicy },
		{
			label: "Exclusão de conta e dados",
			onClick: onNavigateToAccountDeletion,
		},
	].filter(
		(link): link is { label: string; onClick: () => void } =>
			typeof link.onClick === "function",
	);

	const socialLinks = [
		{
			icon: Instagram,
			label: "Instagram",
			href: "https://www.instagram.com/akihub.brasil",
		},
	];

	return (
		<footer className="bg-card border-t border-border mt-auto">
			{/* Main Footer Content */}
			<div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
					{/* Company Info */}
					<div className="space-y-4">
						{onLogoClick ? (
							<button
								type="button"
								onClick={onLogoClick}
								className="flex items-center gap-3 rounded-lg p-1 -m-1 text-left hover:bg-accent/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors w-full sm:w-auto"
								aria-label="Ir para início"
							>
								<SMLogo className="w-10 h-10 shrink-0" />
								<div className="min-w-0 text-left">
									<h3 className="font-semibold text-lg">akihub</h3>
									<p className="text-xs text-muted-foreground">
										Conectando quem troca, garantindo quem recebe
									</p>
								</div>
							</button>
						) : (
							<div className="flex items-center gap-3">
								<SMLogo className="w-10 h-10" />
								<div>
									<h3 className="font-semibold text-lg">akihub</h3>
									<p className="text-xs text-muted-foreground">
										Conectando quem troca, garantindo quem recebe
									</p>
								</div>
							</div>
						)}
						<p className="text-sm text-muted-foreground">
							O marketplace preferido do Brasil para compra e venda de produtos
							novos e usados. Conectamos pessoas e transformamos negócios.
						</p>
						<div className="space-y-2">
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Mail className="w-4 h-4" />
								<span>contato@akihub.com.br</span>
							</div>
						</div>
					</div>

					{/* Categorias com mais anúncios ativos (até 5) */}
					<div>
						<h4 className="font-semibold mb-4">Categorias Populares</h4>
						<ul className="space-y-2">
							{popularCategories.map(({ categoryId, name }) => (
								<li key={categoryId}>
									<button
										onClick={() => onCategoryClick?.(categoryId)}
										className="text-sm text-muted-foreground hover:text-primary transition-colors"
									>
										{name}
									</button>
								</li>
							))}
						</ul>
					</div>

					{/* Company Links */}
					<div>
						<h4 className="font-semibold mb-4">Sobre a Empresa</h4>
						<ul className="space-y-2">
							{companyLinks.map((link) => (
								<li key={link.label}>
									<button
										onClick={link.onClick}
										className="text-sm text-muted-foreground hover:text-primary transition-colors"
									>
										{link.label}
									</button>
								</li>
							))}
						</ul>
					</div>

					{/* Social + store badges */}
					<div className="space-y-6">
						<div>
							<h4 className="font-semibold mb-4">Siga-nos</h4>
							<div className="flex gap-3">
								{socialLinks.map((social) => {
									const Icon = social.icon;
									return (
										<a
											key={social.label}
											href={social.href}
											target="_blank"
											rel="noopener noreferrer"
											aria-label={social.label}
											className="w-10 h-10 bg-secondary hover:bg-primary/10 rounded-full flex items-center justify-center transition-colors group"
										>
											<Icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
										</a>
									);
								})}
							</div>
						</div>

						<div>
							<h4 className="font-semibold mb-4">Instale o app</h4>
							<div className="flex flex-wrap items-center gap-3">
								<a
									href={APPSTORE_URL}
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Baixar na App Store"
									className="inline-flex items-center transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
								>
									{/* Apple-provided PT-BR badge; Apple guidelines require no cropping, minimum 40pt / ~53px height. */}
									<img
										src="/badges/app-store-badge-pt-br.svg"
										alt="Baixar na App Store"
										className="h-10 w-auto"
										loading="lazy"
										decoding="async"
									/>
								</a>
								<a
									href={PLAYSTORE_URL}
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Disponível no Google Play"
									className="inline-flex items-center transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
								>
									{/* Google-provided PT-BR badge. h-10 (40px) matches the Apple badge exactly; the
                      PNG already includes padding so the "Google Play" mark stays legible at this size. */}
									<img
										src="/badges/google-play-badge-pt-br.png"
										alt="Disponível no Google Play"
										className="h-10 w-auto"
										loading="lazy"
										decoding="async"
									/>
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Bottom Bar */}
			<div className="border-t border-border">
				<div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex flex-col md:flex-row justify-between items-center gap-4">
						<p className="text-sm text-muted-foreground text-center md:text-left">
							© {currentYear} akihub. Todos os direitos reservados.
						</p>
						<div className="flex items-center gap-6 text-xs text-muted-foreground">
							<button
								onClick={onNavigateToTerms}
								className="hover:text-primary transition-colors"
							>
								Termos
							</button>
							<button
								onClick={onNavigateToPrivacyPolicy}
								className="hover:text-primary transition-colors"
							>
								Privacidade
							</button>
							<button
								type="button"
								onClick={requestOpenPreferences}
								className="hover:text-primary transition-colors"
							>
								Gerenciar cookies
							</button>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
}
